import React from "react";
import { motion } from "framer-motion";

/**
 * A client screenshot inside a BrowserFrame.
 *
 * One component so the hero, the work showcase and the build sequence resolve the
 * same sources, ratios and fallbacks instead of each repeating the markup.
 *
 * Three widths ship for every site. The frame is only ~350 CSS px wide on a phone,
 * but srcset picks on device pixels, so a DPR-3 phone asks for ~1170px: without a
 * middle rung it skips 800w and takes the full 1800px file, which is what the
 * original single-source markup did. 800/1200/1800 covers DPR 2 and DPR 3 phones
 * and desktop without anyone over-fetching.
 *
 * `image` is the full-size .webp path; the narrower siblings are the same name with
 * a `-800` / `-1200` suffix. If webp fails entirely we fall back to the .jpg
 * original, which means dropping srcset too, or the browser just re-picks a webp.
 */
export function SiteShot({ image, alt, className = "", priority = false, ...motionProps }) {
  const base = image.replace(/\.webp$/, "");

  return (
    <motion.img
      src={image}
      srcSet={`${base}-800.webp 800w, ${base}-1200.webp 1200w, ${image} 1800w`}
      sizes="(min-width: 1024px) 900px, 100vw"
      alt={alt}
      width="1600"
      height="1000"
      decoding="async"
      // The hero shot is the LCP element, so it must not be lazy or deprioritised.
      loading={priority ? "eager" : "lazy"}
      fetchpriority={priority ? "high" : undefined}
      onError={(e) => {
        const el = e.currentTarget;
        if (el.dataset.fellBack) return;
        el.dataset.fellBack = "1";
        el.srcset = "";
        el.src = `${base}.jpg`;
      }}
      className={className}
      {...motionProps}
    />
  );
}

export default SiteShot;
